import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Users, Loader2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/lib/api';
import { toast } from 'sonner';

function GroupDialog({ open, onOpenChange, group, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', color: '#FF8C42' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(group ? { name: group.name || '', description: group.description || '', color: group.color || '#FF8C42' } : { name: '', description: '', color: '#FF8C42' });
  }, [group, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (group?.id) {
        await api.put(`/groups/${group.id}`, form);
      } else {
        await api.post('/groups/', form);
      }
      toast.success('Saved');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/40 sm:max-w-md rounded-2xl">
        <DialogHeader><DialogTitle>{group ? 'Edit Group' : 'New Group'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input data-testid="group-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="group-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="group-color-input" className="h-10 w-14 rounded-lg border border-white/60" />
              <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl bg-white/60 border-white/60">Cancel</Button>
            <Button type="submit" disabled={saving} className="gradient-orange text-white rounded-xl" data-testid="group-save-button">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ open, onOpenChange, group, onAssigned }) {
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !group) return;
    setLoading(true);
    Promise.all([
      api.get('/contacts/'),
      api.get(`/groups/${group.id}/members`),
    ]).then(([c, m]) => {
      setContacts(c.data || []);
      setMembers(new Set((m.data || []).map((x) => x.id)));
    }).finally(() => setLoading(false));
  }, [open, group]);

  const filtered = contacts.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.phone?.toLowerCase().includes(s);
  });

  const toggle = (id) => {
    const next = new Set(members);
    if (next.has(id)) next.delete(id); else next.add(id);
    setMembers(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const orig = new Set(); // refetched below to compute diff
      const m0 = await api.get(`/groups/${group.id}/members`);
      (m0.data || []).forEach((c) => orig.add(c.id));
      const toAdd = [...members].filter((id) => !orig.has(id));
      const toRemove = [...orig].filter((id) => !members.has(id));
      if (toAdd.length) await api.post(`/groups/${group.id}/assign`, { contact_ids: toAdd });
      if (toRemove.length) await api.post(`/groups/${group.id}/unassign`, { contact_ids: toRemove });
      toast.success(`Updated members (+${toAdd.length}, -${toRemove.length})`);
      onAssigned?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/40 sm:max-w-lg rounded-2xl">
        <DialogHeader><DialogTitle>Assign contacts — {group?.name}</DialogTitle></DialogHeader>
        <Input placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-white/70 border-white/60 rounded-xl" data-testid="group-assign-search" />
        <ScrollArea className="h-[320px] rounded-xl border border-white/50 bg-white/40">
          <div className="p-2">
            {loading ? (
              <div className="text-center py-8"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">No contacts found</div>
            ) : filtered.map((c) => (
              <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/60 cursor-pointer">
                <Checkbox checked={members.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`group-assign-checkbox-${c.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.phone}</div>
                </div>
              </label>
            ))}
          </div>
        </ScrollArea>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl bg-white/60 border-white/60">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gradient-orange text-white rounded-xl" data-testid="group-assign-save">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/groups/');
      setGroups(r.data || []);
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Contacts will not be deleted.`)) return;
    try {
      await api.delete(`/groups/${g.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="groups-heading">Groups</h1>
          <p className="text-sm text-muted-foreground mt-1">Organize contacts into segments for targeted campaigns.</p>
        </div>
        <Button onClick={() => { setEditTarget(null); setDialogOpen(true); }} className="gradient-orange text-white rounded-xl" data-testid="groups-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Group
        </Button>
      </div>

      {loading ? (
        <div className="glass-card p-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : !groups.length ? (
        <div className="glass-card p-10 text-center">
          <div className="inline-flex h-12 w-12 rounded-2xl bg-[#FFE2D1] text-[#9A3412] items-center justify-center mb-3"><Users className="h-6 w-6" /></div>
          <div className="text-sm text-muted-foreground mb-3">No groups yet. Create your first audience segment.</div>
          <Button onClick={() => setDialogOpen(true)} className="gradient-orange text-white rounded-xl">Create Group</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <div key={g.id} className="glass-card p-5" data-testid={`group-card-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ background: g.color || '#FF8C42' }} />
                    <div className="font-semibold">{g.name}</div>
                  </div>
                  {g.description && <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{g.description}</div>}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setEditTarget(g); setDialogOpen(true); }} aria-label="Edit" data-testid={`group-edit-${g.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(g)} aria-label="Delete" data-testid={`group-delete-${g.id}`}><Trash2 className="h-4 w-4 text-[#9F1239]" /></Button>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm"><span className="font-semibold">{g.member_count || 0}</span> members</div>
                <Button variant="outline" size="sm" className="rounded-xl bg-white/60 border-white/60" onClick={() => setAssignTarget(g)} data-testid={`group-assign-button-${g.id}`}>
                  <UserPlus className="h-4 w-4 mr-1.5" /> Manage
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <GroupDialog open={dialogOpen} onOpenChange={setDialogOpen} group={editTarget} onSaved={load} />
      <AssignDialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)} group={assignTarget} onAssigned={load} />
    </div>
  );
}
